import { z } from "zod";

export const MEGASTRUCTURE_VISUALIZATION_KIND = "stacked_megastructure_visualization";

const colorSchema = z.string().regex(/^#[0-9a-f]{6}$/i, "expected a six-digit hex colour");
const positiveDimension = z.number().finite().positive().max(100_000);

export const megastructureVisualizationPartSchema = z.enum([
  "landscape",
  "disk",
  "perimeter_wall",
  "central_pillar",
  "tier_stack",
  "appearance",
  "structure_array",
  "additions",
]);

export const megastructureStructureArraySchema = z.object({
  mode: z.literal("seeded_scatter_v1"),
  instanceCount: z.number().int().min(1).max(64),
  spreadDiameterM: z.number().finite().min(0).max(100_000),
  spacingM: z.number().finite().positive().max(100_000).default(5_000),
  randomness: z.number().finite().min(0).max(1),
  seed: z.number().int().min(0).max(2_147_483_647),
  minimumClearanceM: z.number().finite().min(0).max(10_000).default(500),
  autoExpandLandscape: z.boolean(),
  groundPaddingM: z.number().finite().min(0).max(10_000),
}).strict();

export const radialTowerArrayAdditionSchema = z.object({
  id: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
  kind: z.literal("radial_tower_array"),
  anchor: z.literal("perimeter_wall_top"),
  shape: z.enum(["cylinder", "box"]),
  spacingDeg: z.number().finite().min(1).max(360),
  angularOffsetDeg: z.number().finite().min(-360).max(360),
  diameterM: positiveDimension.max(1_000),
  heightM: positiveDimension.max(5_000),
  radialOffsetM: z.number().finite().min(-2_000).max(2_000),
  baseOffsetM: z.number().finite().min(-1_000).max(5_000),
  appliesTo: z.enum(["every_tier", "first_tier"]),
  replicationScope: z.enum(["anchor_stack", "each_full_stack"]).default("anchor_stack"),
  color: colorSchema,
}).strict();

export const geographicCityContextAdditionSchema = z.object({
  id: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
  kind: z.literal("geographic_city_context"),
  preset: z.literal("paris_eiffel_v1"),
  anchorLandmark: z.literal("eiffel_tower"),
  structureDirection: z.enum(["east", "west", "north", "south"]),
  clearanceM: z.number().finite().min(0).max(5_000),
  detail: z.literal("schematic_massing"),
}).strict();

export const megastructureVisualizationAdditionSchema = z.discriminatedUnion("kind", [
  radialTowerArrayAdditionSchema,
  geographicCityContextAdditionSchema,
]);

export const stackedMegastructureVisualizationSchema = z.object({
  schemaVersion: z.literal("worldloom.stacked-megastructure-visualization.v1"),
  scope: z.literal("candidate_visualization"),
  sceneUid: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
  label: z.string().trim().min(1).max(240),
  coordinateFrame: z.object({
    horizontalAxes: z.literal("x_east_z_north"),
    verticalAxis: z.literal("y_up"),
    units: z.literal("meters"),
  }).strict(),
  landscape: z.object({
    widthM: positiveDimension,
    depthM: positiveDimension,
    thicknessM: positiveDimension.max(1_000),
  }).strict(),
  tiers: z.object({
    count: z.number().int().min(1).max(100),
    verticalPitchM: positiveDimension.max(10_000),
    disk: z.object({
      diameterM: positiveDimension.max(20_000),
      heightM: positiveDimension.max(2_000),
    }).strict(),
    perimeterWall: z.object({
      radialThicknessM: positiveDimension.max(2_000),
      heightM: positiveDimension.max(5_000),
    }).strict(),
    centralPillar: z.object({
      diameterM: positiveDimension.max(5_000),
      heightM: positiveDimension.max(10_000),
    }).strict(),
  }).strict(),
  appearance: z.object({
    landscapeColor: colorSchema,
    diskColor: colorSchema,
    wallColor: colorSchema,
    pillarColor: colorSchema,
  }).strict(),
  structureArray: megastructureStructureArraySchema.default({
    mode: "seeded_scatter_v1",
    instanceCount: 1,
    spreadDiameterM: 30_000,
    spacingM: 5_000,
    randomness: 0.65,
    seed: 20_260_808,
    minimumClearanceM: 500,
    autoExpandLandscape: true,
    groundPaddingM: 1_000,
  }),
  additions: z.array(megastructureVisualizationAdditionSchema).max(64).default([]),
  edit: z.object({
    source: z.enum(["bootstrap", "manual", "llm"]),
    changedParts: z.array(megastructureVisualizationPartSchema).max(6),
    request: z.string().trim().max(10_000).optional(),
    summary: z.string().trim().min(1).max(1_000),
    blenderSource: z.object({
      version: z.literal("5.2.0"),
      glbUrl: z.string().startsWith("/map-artifacts/").max(500),
      glbSha256: z.string().regex(/^[0-9a-f]{64}$/),
      generatorPath: z.literal("scripts/create_stacked_megastructure_blender.py"),
      buildManifestPath: z.literal("blender_outputs/stacked_megastructure_v1/build_manifest.json"),
    }).strict(),
    llm: z.object({
      model: z.string().min(1).max(240),
      servedModel: z.string().min(1).max(240),
      generationId: z.string().min(1).max(240),
    }).strict().optional(),
  }).strict(),
}).strict().superRefine((scene, ctx) => {
  const radiusM = scene.tiers.disk.diameterM / 2;
  if (scene.tiers.perimeterWall.radialThicknessM >= radiusM) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["tiers", "perimeterWall", "radialThicknessM"],
      message: "wall thickness must be smaller than the disk radius",
    });
  }
  if (scene.tiers.centralPillar.diameterM >= scene.tiers.disk.diameterM) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["tiers", "centralPillar", "diameterM"],
      message: "central pillar must be smaller than the disk",
    });
  }
  if (scene.structureArray.instanceCount > 1 && scene.structureArray.spreadDiameterM < scene.tiers.disk.diameterM) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["structureArray", "spreadDiameterM"],
      message: "whole-structure spread must be at least one disk diameter when more than one structure is present",
    });
  }
  if (scene.structureArray.instanceCount > 1) {
    const separationM = scene.tiers.disk.diameterM + scene.structureArray.minimumClearanceM;
    const cellSizeM = scene.structureArray.spacingM;
    if (cellSizeM < separationM) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["structureArray", "spacingM"],
        message: "sky-elevator spacing must be at least the disk diameter plus minimum clearance",
      });
    }
    const jitterAxisM = Math.max(0, (cellSizeM - separationM) / 2) * scene.structureArray.randomness;
    const jitterRadiusM = Math.SQRT2 * jitterAxisM;
    const distributionRadiusM = scene.structureArray.spreadDiameterM / 2;
    const latticeLimit = Math.floor(Math.max(0, distributionRadiusM - jitterRadiusM) / cellSizeM);
    let capacity = 1;
    for (let x = -latticeLimit; x <= latticeLimit; x += 1) {
      for (let z = -latticeLimit; z <= latticeLimit; z += 1) {
        if ((x !== 0 || z !== 0) && Math.hypot(x * cellSizeM, z * cellSizeM) + jitterRadiusM <= distributionRadiusM) capacity += 1;
      }
    }
    if (scene.structureArray.instanceCount > capacity) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["structureArray", "spreadDiameterM"],
        message: `whole-structure spread fits at most ${capacity} structures at the requested randomness and clearance`,
      });
    }
  }
  const additionIds = scene.additions.map((addition) => addition.id);
  if (new Set(additionIds).size !== additionIds.length) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["additions"], message: "addition IDs must be unique" });
  }
  for (const [index, addition] of scene.additions.entries()) {
    if (addition.kind !== "radial_tower_array") continue;
    const innerRadiusM = radiusM - scene.tiers.perimeterWall.radialThicknessM;
    const centreRadiusM = radiusM - scene.tiers.perimeterWall.radialThicknessM / 2 + addition.radialOffsetM;
    const towerInnerRadiusM = centreRadiusM - addition.diameterM / 2;
    const towerOuterRadiusM = centreRadiusM + addition.diameterM / 2;
    if (centreRadiusM <= 0 || towerOuterRadiusM < innerRadiusM || towerInnerRadiusM > radiusM) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["additions", index, "radialOffsetM"],
        message: "wall tower array must remain attached to the perimeter wall",
      });
    }
  }
  if (scene.additions.filter((addition) => addition.kind === "geographic_city_context").length > 1) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["additions"], message: "only one geographic city context may be active" });
  }
  const radialInstanceCount = scene.additions.reduce((total, addition) => {
    if (addition.kind !== "radial_tower_array") return total;
    const angles = Math.max(1, Math.ceil((360 - 1e-9) / addition.spacingDeg));
    const tiers = addition.appliesTo === "every_tier" ? scene.tiers.count : 1;
    // Radial wall towers are a decoration on the canonical/original elevator.
    // Replicating sky-elevator roots must not silently multiply decorations.
    const structures = addition.replicationScope === "each_full_stack" ? scene.structureArray.instanceCount : 1;
    return total + angles * tiers * structures;
  }, 0);
  const baseInstanceCount = scene.structureArray.instanceCount * scene.tiers.count * 3;
  const cityInstanceAllowance = scene.additions.some((addition) => addition.kind === "geographic_city_context") ? 512 : 0;
  const renderedInstanceWork = (baseInstanceCount + radialInstanceCount) * 2 + cityInstanceAllowance + 1;
  if (renderedInstanceWork > 80_000) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["structureArray", "instanceCount"],
      message: "live whole-structure array exceeds the 80,000-matrix shaded-plus-wire visualization budget",
    });
  }
});

export type StackedMegastructureVisualization = z.infer<typeof stackedMegastructureVisualizationSchema>;
export type MegastructureVisualizationPart = z.infer<typeof megastructureVisualizationPartSchema>;
export type MegastructureVisualizationAddition = z.infer<typeof megastructureVisualizationAdditionSchema>;

export const megastructureVisualizationOperationPathSchema = z.enum([
  "landscape.widthM",
  "landscape.depthM",
  "landscape.thicknessM",
  "tiers.count",
  "tiers.verticalPitchM",
  "tiers.disk.diameterM",
  "tiers.disk.heightM",
  "tiers.perimeterWall.radialThicknessM",
  "tiers.perimeterWall.heightM",
  "tiers.centralPillar.diameterM",
  "tiers.centralPillar.heightM",
  "structureArray.instanceCount",
  "structureArray.spreadDiameterM",
  "structureArray.spacingM",
  "structureArray.randomness",
  "structureArray.seed",
  "structureArray.minimumClearanceM",
  "structureArray.groundPaddingM",
  "appearance.landscapeColor",
  "appearance.diskColor",
  "appearance.wallColor",
  "appearance.pillarColor",
]);

const megastructureVisualizationSetOperationSchema = z.object({
  path: megastructureVisualizationOperationPathSchema,
  value: z.union([z.number().finite(), colorSchema]),
}).strict();

const megastructureVisualizationUpsertAdditionOperationSchema = z.object({
  op: z.literal("upsert_addition"),
  addition: megastructureVisualizationAdditionSchema,
}).strict();

const megastructureVisualizationRemoveAdditionOperationSchema = z.object({
  op: z.literal("remove_addition"),
  additionId: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
}).strict();

export const megastructureVisualizationOperationSchema = z.union([
  megastructureVisualizationSetOperationSchema,
  megastructureVisualizationUpsertAdditionOperationSchema,
  megastructureVisualizationRemoveAdditionOperationSchema,
]);

export const megastructureVisualizationReplySchema = z.object({
  summary: z.string().trim().min(1).max(1_000),
  operations: z.array(megastructureVisualizationOperationSchema).min(1).max(20),
}).strict();

export type MegastructureVisualizationOperation = z.infer<typeof megastructureVisualizationOperationSchema>;
export type MegastructureVisualizationReply = z.infer<typeof megastructureVisualizationReplySchema>;

export function createDefaultMegastructureVisualization(): StackedMegastructureVisualization {
  return stackedMegastructureVisualizationSchema.parse({
    schemaVersion: "worldloom.stacked-megastructure-visualization.v1",
    scope: "candidate_visualization",
    sceneUid: "thirty_tier_earth_megastructure_visualization",
    label: "Thirty-Tier Earth Megastructure",
    coordinateFrame: { horizontalAxes: "x_east_z_north", verticalAxis: "y_up", units: "meters" },
    landscape: { widthM: 3_600, depthM: 3_600, thicknessM: 1 },
    tiers: {
      count: 30,
      verticalPitchM: 1_000,
      disk: { diameterM: 3_000, heightM: 50 },
      perimeterWall: { radialThicknessM: 10, heightM: 200 },
      centralPillar: { diameterM: 50, heightM: 1_000 },
    },
    appearance: {
      landscapeColor: "#27372d",
      diskColor: "#707983",
      wallColor: "#c5864e",
      pillarColor: "#d8c7a0",
    },
    structureArray: {
      mode: "seeded_scatter_v1",
      instanceCount: 1,
      spreadDiameterM: 30_000,
      spacingM: 5_000,
      randomness: 0.65,
      seed: 20_260_808,
      minimumClearanceM: 500,
      autoExpandLandscape: true,
      groundPaddingM: 1_000,
    },
    additions: [],
    edit: {
      source: "bootstrap",
      changedParts: ["landscape", "disk", "perimeter_wall", "central_pillar", "tier_stack", "appearance"],
      summary: "Blender 5.2 source visualization",
      blenderSource: {
        version: "5.2.0",
        glbUrl: "/map-artifacts/stacked_megastructure_30_tiers.glb",
        glbSha256: "7ce630e5cb8f4af28f262480737cbbfe4c5823a07aa46a48bcf9e40e37ad1b0e",
        generatorPath: "scripts/create_stacked_megastructure_blender.py",
        buildManifestPath: "blender_outputs/stacked_megastructure_v1/build_manifest.json",
      },
    },
  });
}

const PART_BY_PATH: Record<z.infer<typeof megastructureVisualizationOperationPathSchema>, MegastructureVisualizationPart> = {
  "landscape.widthM": "landscape",
  "landscape.depthM": "landscape",
  "landscape.thicknessM": "landscape",
  "tiers.count": "tier_stack",
  "tiers.verticalPitchM": "tier_stack",
  "tiers.disk.diameterM": "disk",
  "tiers.disk.heightM": "disk",
  "tiers.perimeterWall.radialThicknessM": "perimeter_wall",
  "tiers.perimeterWall.heightM": "perimeter_wall",
  "tiers.centralPillar.diameterM": "central_pillar",
  "tiers.centralPillar.heightM": "central_pillar",
  "structureArray.instanceCount": "structure_array",
  "structureArray.spreadDiameterM": "structure_array",
  "structureArray.spacingM": "structure_array",
  "structureArray.randomness": "structure_array",
  "structureArray.seed": "structure_array",
  "structureArray.minimumClearanceM": "structure_array",
  "structureArray.groundPaddingM": "structure_array",
  "appearance.landscapeColor": "appearance",
  "appearance.diskColor": "appearance",
  "appearance.wallColor": "appearance",
  "appearance.pillarColor": "appearance",
};

function setPath(target: Record<string, unknown>, path: string, value: unknown) {
  const segments = path.split(".");
  let cursor = target;
  for (const segment of segments.slice(0, -1)) cursor = cursor[segment] as Record<string, unknown>;
  cursor[segments.at(-1)!] = value;
}

/** Apply the LLM's small allow-listed operation set; full-state replacement is never accepted. */
export function applyMegastructureVisualizationOperations(
  input: StackedMegastructureVisualization,
  operations: readonly MegastructureVisualizationOperation[],
  edit: Pick<StackedMegastructureVisualization["edit"], "source" | "summary" | "request">,
): StackedMegastructureVisualization {
  const source = stackedMegastructureVisualizationSchema.parse(input);
  const parsedOperations = operations.map((operation) => megastructureVisualizationOperationSchema.parse(operation));
  const next = structuredClone(source) as unknown as Record<string, unknown>;
  for (const operation of parsedOperations) {
    if (!("op" in operation)) {
      setPath(next, operation.path, operation.value);
    } else if (operation.op === "upsert_addition") {
      const additions = next.additions as MegastructureVisualizationAddition[];
      const index = additions.findIndex((addition) => addition.id === operation.addition.id);
      if (index < 0) additions.push(operation.addition); else additions[index] = operation.addition;
    } else {
      next.additions = (next.additions as MegastructureVisualizationAddition[]).filter((addition) => addition.id !== operation.additionId);
    }
  }
  const changedParts = parsedOperations.map((operation) => "op" in operation
    ? "additions" as const
    : PART_BY_PATH[operation.path]);
  next.edit = {
    ...source.edit,
    ...edit,
    changedParts: [...new Set(changedParts)],
  };
  return stackedMegastructureVisualizationSchema.parse(next);
}

export function megastructureVisualizationOverallHeightM(scene: StackedMegastructureVisualization): number {
  const parsed = stackedMegastructureVisualizationSchema.parse(scene);
  const lastBase = (parsed.tiers.count - 1) * parsed.tiers.verticalPitchM;
  const stackedCoreTop = lastBase + Math.max(
    parsed.tiers.disk.heightM,
    parsed.tiers.disk.heightM + parsed.tiers.perimeterWall.heightM,
    parsed.tiers.centralPillar.heightM,
  );
  const additionTops = parsed.additions.map((addition) => addition.kind === "radial_tower_array"
    ? (addition.appliesTo === "every_tier" ? lastBase : 0)
      + parsed.tiers.disk.heightM
      + parsed.tiers.perimeterWall.heightM
      + addition.baseOffsetM
      + addition.heightM
    : 330);
  return Math.max(stackedCoreTop, ...additionTops);
}
